import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetBatchDetail,
  useGetManagementBatchDetail,
  bulkUploadsQueryKeys,
} from "../../../hooks/queries/useBulkUploadsQueries";
import { TransactionQueryKeys } from "../../../hooks/queries/useTransactionQueries";

export const useBatchDetail = (id, isSuperAdmin = false) => {
  const queryClient = useQueryClient();
  const merchantQuery = useGetBatchDetail(id, { enabled: !isSuperAdmin });
  const managementQuery = useGetManagementBatchDetail(id, { enabled: isSuperAdmin });

  const { data, isLoading, isError, refetch } = isSuperAdmin ? managementQuery : merchantQuery;

  // Fetching this batch's real status is the moment we might learn the
  // Bulk Payments list (and the Transactions list, which shows the same
  // underlying records) is showing a stale status for it — mark both
  // caches dirty so they refetch fresh data the next time they're viewed,
  // instead of silently reusing whatever was cached before this visit.
  useEffect(() => {
    if (!data) return;
    queryClient.invalidateQueries({ queryKey: bulkUploadsQueryKeys.all });
    queryClient.invalidateQueries({ queryKey: TransactionQueryKeys.all });
  }, [data, queryClient]);

  return {
    batchId: data?.batchId ?? null,
    submittedBy: data?.submittedBy ?? null,
    submittedOn: data?.submittedOn ?? null,
    status: data?.status ?? null,
    paymentChannel: data?.paymentChannel ?? null,
    recipientCount: data?.recipientCount ?? null,
    totalTransactions: data?.totalTransactions ?? null,
    successCount: data?.successCount ?? null,
    pendingCount: data?.pendingCount ?? null,
    failedCount: data?.failedCount ?? null,
    amount: data?.amount ?? null,
    charges: data?.charges ?? null,
    totalAmount: data?.totalAmount ?? null,
    merchantName: data?.merchant?.merchantName ?? null,
    isLoading,
    isError,
    refetch,
  };
};

export default useBatchDetail;
